import React, { useState } from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import TableToolbar from "components/MDTable/tableToolbar";
import TableHeader from "components/MDTable/tableHeader";
import { Order } from "components/MDTable/helper";
import { ITableHeader } from "types/Table";
import { AllUsers } from "types/AllUsers";
import TablePaginationActions from "components/MDTable/tablePagination";
import MDBox from "components/MDBox";
import InputDebounce from "../../components/MDInput/inputDebounce";
import { useSelector } from "react-redux";
import { getLoadingStatus } from "redux/slices/allUsersSlice";
import CircularProgress from "@mui/material/CircularProgress";
import { NewUser } from "types/Authentication";
import { stringTimeStampToDate } from "helper/date-helper";
import MessageIcon from "@mui/icons-material/Message";
import IconButton from "@mui/material/IconButton";
import Checkbox from "@mui/material/Checkbox";
import Tooltip from "@mui/material/Tooltip";

type TableType = {
  header: ITableHeader[];
  rows: AllUsers;
  onPageChanged: (newPage: number) => void;
  handleUserSearch: (searchInput: string) => void;
  onReload: (currentPage: number) => void;
  addNewOrEditUser: (isEdit: boolean, selectedUser?: NewUser) => void;
};
const SortedTable: React.FC<TableType> = (props) => {
  const { header, rows, onPageChanged, handleUserSearch, onReload } = props;
  const { customers } = rows;
  const [order, setOrder] = useState<Order>("desc");
  const [orderBy, setOrderBy] = useState<string>("");
  const [selected, setSelected] = useState<readonly string[]>([]);
  const [page, setPage] = useState<number>(0);
  const isLoading = useSelector(getLoadingStatus);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: string
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelecteds = customers.map((n) => n.customer_id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event: React.MouseEvent<unknown>, name: string) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected: readonly string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    onPageChanged(newPage);
    setPage(newPage);
  };

  const isSelected = (name: string) => selected.indexOf(name) !== -1;

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", mb: 2 }}>
        <TableToolbar
          numSelected={selected.length}
          tableName={"Users"}
          onReload={() => onReload(page)}
        />
        <MDBox
          display={"inline-flex"}
          justifyContent={"space-between"}
          width={"100%"}
          padding={"10px"}
        >
          <InputDebounce
            type="text"
            name="username"
            placeholder="Username"
            onChange={handleUserSearch}
            width={"60%"}
          />
        </MDBox>
        {isLoading ? (
          <MDBox display={"flex"} justifyContent={"center"} mt={3}>
            <CircularProgress color={"primary"} />
          </MDBox>
        ) : (
          <TableContainer>
            <Table
              sx={{ minWidth: 750 }}
              aria-labelledby="tableTitle"
              size={"medium"}
            >
              <TableHeader
                numSelected={selected.length}
                order={order}
                orderBy={orderBy}
                disableCheckbox
                onSelectAllClick={handleSelectAllClick}
                onRequestSort={handleRequestSort}
                rowCount={customers.length}
                headCells={header}
              />
              <TableBody>
                {customers.map((row, index) => {
                  const isItemSelected = isSelected(row.customer_id);
                  const labelId = `enhanced-table-checkbox-${index}`;
                  return (
                    <TableRow
                      hover
                      onClick={(event) => handleClick(event, row.customer_id)}
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      key={row.customer_id}
                      selected={isItemSelected}
                    >
                      <TableCell padding="checkbox" align="right">
                        <Checkbox
                          color="primary"
                          checked={isItemSelected}
                          inputProps={{
                            "aria-labelledby": labelId
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">{`${row.customer_name}`}</TableCell>
                      <TableCell align="right">{row.stamps}</TableCell>
                      <TableCell align="right">
                        {stringTimeStampToDate(row.last_stamp)}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Coming Soon">
                          <IconButton
                            onClick={(e) => {
                              //Prevent whole  row is being selected
                              e.stopPropagation();
                              e.preventDefault();
                              //  addNewOrViewNotifications(true, row);
                            }}
                          >
                            <MessageIcon color="primary" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <MDBox display={"inline-flex"} width={"100%"} justifyContent={"end"}>
          <InputDebounce
            type="number"
            name="search page"
            placeholder="search specify page"
            onChange={(value: string) => {
              const page = Number(value);
              handleChangePage(null, page === 0 ? 1 : page - 1);
            }}
            width={"10%"}
          />
          <TablePagination
            rowsPerPageOptions={[25]}
            component="div"
            count={rows.total}
            rowsPerPage={25}
            page={rows.total <= 0 ? 0 : page}
            onPageChange={handleChangePage}
            ActionsComponent={TablePaginationActions}
          />
        </MDBox>
      </Paper>
    </Box>
  );
};
export default SortedTable;
